import * as m from '@staiter/i18n';
import type { Counterparty, CounterpartyKind } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Briefcase, Plus, Power, PowerOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState, PageHeader, Pagination, Table, type TableColumn } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Checkbox, Input, Select } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status/ProvenanceChip';
import {
  bulkCounterpartyAction,
  fetchCounterparties,
  updateCounterparty,
} from '~/lib/api/counterparties';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import shared from './_auth._shared.module.css';
import styles from './_auth.counterparties.module.css';

/**
 * S14-FE.2 — Counterparties list (search + kind filter + push-1c button placeholder).
 */

export const Route = createFileRoute('/_auth/counterparties/')({
  staticData: { minMainWidth: 960 },
  component: CounterpartiesPage,
});

const KIND_LABELS: Record<CounterpartyKind, () => string> = {
  customer: m.cp_kind_customer,
  supplier: m.cp_kind_supplier,
  employee: m.cp_kind_employee,
  other: m.cp_kind_other,
};
const TAX_LABELS: Record<string, () => string> = {
  vat_payer: m.cp_tax_vat_payer,
  simplified: m.cp_tax_simplified,
  patent: m.cp_tax_patent,
  individual: m.cp_tax_individual,
};

function CounterpartiesPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<CounterpartyKind | ''>('');
  const [q, setQ] = useState('');
  // integrations F7 — provenance / scope / needs_review фильтры.
  const [source, setSource] = useState<string>('');
  const [scope, setScope] = useState<'' | 'holding' | 'entity'>('');
  const [needsReview, setNeedsReview] = useState(false);
  // F7-fix — пагинация (список отдавал только первые 100 без навигации).
  const [pagination, setPagination] = useState<{ limit: number; offset: number }>({
    limit: 100,
    offset: 0,
  });
  // SET-AUDIT-7: selection state for bulk actions.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // F7-fix — смена фильтра возвращает на первую страницу.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset offset on filter change only
  useEffect(() => {
    setPagination((p) => (p.offset === 0 ? p : { ...p, offset: 0 }));
  }, [kind, q, source, scope, needsReview]);

  const query = useQuery({
    queryKey: ['counterparties', { kind, q, source, scope, needsReview, ...pagination }],
    queryFn: () =>
      fetchCounterparties({
        ...(kind ? { kind } : {}),
        ...(q ? { q } : {}),
        ...(source ? { source_system: source } : {}),
        ...(scope ? { scope } : {}),
        ...(needsReview ? { needs_review: true } : {}),
        ...pagination,
      }),
  });

  const rows = query.data?.items ?? [];
  // access-control (F7) — колонка компании видна только в holding-агрегате (>1 компании в выдаче).
  const entityNames = query.data?.entities ?? {};

  // SET-AUDIT-11: quick toggle is_active без открытия detail page.
  const toggleActiveMut = useMutation({
    mutationFn: (cp: Counterparty) => updateCounterparty(cp.id, { is_active: !cp.is_active }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['counterparties'] });
      toast.push({
        variant: 'success',
        title: updated.is_active ? 'Контрагент активирован' : 'Контрагент деактивирован',
        description: updated.name_ru,
      });
    },
    onError: (err) => {
      toast.push({
        variant: 'error',
        title: 'Не удалось изменить статус',
        description: renderApiErrorMessage(err),
      });
    },
  });

  // SET-AUDIT-7: bulk activate / deactivate.
  const bulkMut = useMutation({
    mutationFn: bulkCounterpartyAction,
    onSuccess: (res, vars) => {
      void qc.invalidateQueries({ queryKey: ['counterparties'] });
      setSelected(new Set());
      const skippedCount = res.skipped.length;
      const skippedHint =
        skippedCount > 0 ? ` Пропущено: ${skippedCount} (открытые документы или отсутствуют)` : '';
      toast.push({
        variant: skippedCount === res.total ? 'warning' : 'success',
        title:
          vars.action === 'activate'
            ? `Активировано: ${res.succeeded.length}/${res.total}`
            : `Деактивировано: ${res.succeeded.length}/${res.total}`,
        description: skippedHint || undefined,
      });
    },
    onError: (err) => {
      toast.push({
        variant: 'error',
        title: 'Массовая операция не выполнена',
        description: renderApiErrorMessage(err),
      });
    },
  });

  const columns: TableColumn<Counterparty>[] = useMemo(
    () => [
      {
        id: 'name',
        header: m.cp_column_name(),
        accessor: (r) => (
          <span className={styles.nameCell}>
            <Link to="/counterparties/$id" params={{ id: r.id }} className={shared.accentLink}>
              {r.name_ru}
            </Link>
            {r.needs_review && (
              <span className={styles.reviewBadge} title={m.cp_review_hint()}>
                <AlertTriangle size={11} aria-hidden="true" />
                {m.cp_badge_needs_review()}
              </span>
            )}
          </span>
        ),
        sticky: true,
      },
      {
        id: 'source',
        header: m.cp_column_source(),
        accessor: (r) => <ProvenanceChip source={r.source_system} />,
        width: '120px',
      },
      {
        // integrations F7 — scope: holding CP «Общий (холдинг)» vs компания.
        id: 'company',
        header: m.cp_field_company(),
        accessor: (r: Counterparty) =>
          r.holding_id ? (
            <span className={styles.scopeHolding}>{m.cp_scope_holding()}</span>
          ) : (
            (entityNames[r.entity_id ?? '']?.name ?? '—')
          ),
        width: '180px',
      },
      {
        id: 'kind',
        header: m.cp_column_kind(),
        accessor: (r) => KIND_LABELS[r.kind](),
        width: '120px',
      },
      {
        id: 'bin_iin',
        header: m.cp_column_bin_iin(),
        accessor: (r) => r.bin_iin ?? '—',
        mono: true,
        width: '140px',
      },
      {
        id: 'tax_status',
        header: m.cp_column_tax_status(),
        accessor: (r) => (TAX_LABELS[r.tax_status] ?? (() => r.tax_status))(),
        width: '150px',
      },
      {
        id: 'currency',
        header: m.cp_column_currency(),
        accessor: (r) => r.default_currency,
        width: '90px',
      },
      {
        id: 'active',
        header: m.cp_column_active(),
        // FE-P2-5 (QA 2026-06-04): «—» неинформативно (читается как «нет данных»),
        // тогда как поле boolean всегда true/false. ✓/✗ дают clear semantic + tooltip.
        accessor: (r) => (
          <span
            style={{ color: r.is_active ? 'var(--ok)' : 'var(--text-muted)' }}
            title={r.is_active ? 'Активный' : 'Неактивный'}
          >
            {r.is_active ? '✓' : '✗'}
          </span>
        ),
        width: '80px',
        align: 'center',
      },
    ],
    [entityNames],
  );

  const userScope = useUserScope();
  const canCreate = canDo(userScope, 'counterparty', 'create');
  // F38 (WHS-F38): (де)активация и выбор строк — только с правом на изменение.
  const canUpdate = canDo(userScope, 'counterparty', 'update');
  return (
    <div>
      <PageHeader
        title={m.phase2_counterparties_title()}
        actions={
          // F38 (WHS-F38): создание — только роли с правом `counterparty:create`.
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus size={16} />}
              onClick={() => void navigate({ to: '/counterparties/new' })}
            >
              {m.cp_button_new()}
            </Button>
          ) : undefined
        }
      />

      <div className={styles.filterBar}>
        <Input
          label={m.cp_filter_q_placeholder()}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={m.cp_filter_q_placeholder()}
          className={styles.inputFlex}
          aria-label={m.cp_filter_q_placeholder()}
        />
        <Select
          label={m.cp_column_kind()}
          hideLabel
          value={kind}
          onChange={(e) => setKind((e.target.value as CounterpartyKind) || '')}
          options={[
            { value: '', label: m.cp_filter_all_kinds() },
            { value: 'customer', label: m.cp_kind_customer() },
            { value: 'supplier', label: m.cp_kind_supplier() },
            { value: 'employee', label: m.cp_kind_employee() },
            { value: 'other', label: m.cp_kind_other() },
          ]}
        />
        {/* integrations F7 — provenance source filter */}
        <Select
          label={m.cp_filter_source()}
          hideLabel
          value={source}
          onChange={(e) => setSource(e.target.value)}
          options={[
            { value: '', label: m.cp_filter_all_sources() },
            { value: 'staiter', label: 'STAITER' },
            { value: '1c', label: '1С' },
            { value: 'bitrix', label: 'Bitrix' },
            { value: 'adesk', label: 'Adesk' },
          ]}
        />
        {/* integrations F7 — scope (holding/entity) filter */}
        <Select
          label={m.cp_filter_scope()}
          hideLabel
          value={scope}
          onChange={(e) => setScope((e.target.value as 'holding' | 'entity') || '')}
          options={[
            { value: '', label: m.cp_filter_all_scopes() },
            { value: 'holding', label: m.cp_scope_holding() },
            { value: 'entity', label: m.cp_scope_entity() },
          ]}
        />
        {/* integrations F7 — needs_review filter */}
        <Checkbox
          label={m.cp_filter_needs_review()}
          checked={needsReview}
          onChange={(e) => setNeedsReview(e.target.checked)}
        />
      </div>

      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.cp_empty_title()} icon={<Briefcase size={32} />} />
      ) : (
        <Table<Counterparty>
          data={rows}
          columns={columns}
          rowKey={(r) => r.id}
          ariaLabel={m.phase2_counterparties_title()}
          loading={query.isLoading}
          emptyTitle={m.cp_empty_title()}
          stickyFirstColumn
          selectable={canUpdate}
          selectedIds={selected}
          onSelectionChange={setSelected}
          {...(canUpdate
            ? {
                rowActions: (r: Counterparty) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    leadingIcon={
                      r.is_active ? (
                        <PowerOff size={14} aria-hidden="true" />
                      ) : (
                        <Power size={14} aria-hidden="true" />
                      )
                    }
                    onClick={() => toggleActiveMut.mutate(r)}
                    disabled={toggleActiveMut.isPending}
                    title={r.is_active ? m.ui_deaktivirovat() : m.ui_aktivirovat()}
                    aria-label={r.is_active ? m.ui_deaktivirovat() : m.ui_aktivirovat()}
                  >
                    {r.is_active ? m.ui_deaktivirovat() : m.ui_aktivirovat()}
                  </Button>
                ),
              }
            : {})}
        />
      )}
      {/* F7-fix — пагинация (иначе видно только первые 100 из N). */}
      {(query.data?.total_count ?? 0) > pagination.limit && (
        <Pagination
          totalCount={query.data?.total_count ?? 0}
          limit={pagination.limit}
          offset={pagination.offset}
          onChange={setPagination}
        />
      )}
      {/* SET-AUDIT-7: sticky bulk action bar при ≥1 selected. */}
      {canUpdate && selected.size > 0 && (
        <div
          role="toolbar"
          aria-label="Массовые действия"
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            padding: '10px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 100,
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {m.ui_vybrano()} <strong>{selected.size}</strong>
          </span>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Power size={14} aria-hidden="true" />}
            onClick={() => bulkMut.mutate({ ids: Array.from(selected), action: 'activate' })}
            disabled={bulkMut.isPending}
          >
            {m.ui_aktivirovat()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<PowerOff size={14} aria-hidden="true" />}
            onClick={() => bulkMut.mutate({ ids: Array.from(selected), action: 'deactivate' })}
            disabled={bulkMut.isPending}
          >
            {m.ui_deaktivirovat()}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {m.ui_snyat_vydelenie()}
          </Button>
        </div>
      )}
    </div>
  );
}
